import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ROUTES } from '../../../constants/routes';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function boolToYn(b) {
  if (b === true) return 'Y';
  if (b === false) return 'N';
  return '';
}

function ynToBool(s) {
  if (s === 'Y') return true;
  if (s === 'N') return false;
  return null;
}

function HomesiteInspectionForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get('view') === '1' || searchParams.get('view') === 'true';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [inspectorName, setInspectorName] = useState('');
  const [inspectedAtLocal, setInspectedAtLocal] = useState('');
  const [locationId, setLocationId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [warningNumber, setWarningNumber] = useState('none');
  const [dueDateCorrections, setDueDateCorrections] = useState('');
  const [included, setIncluded] = useState({});
  const [approvals, setApprovals] = useState({});
  const [resolved, setResolved] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.id) === String(locationId)) || null,
    [locations, locationId]
  );

  const readOnly = useMemo(() => {
    if (!data?.inspection) return true;
    return viewOnly || data.inspection.status === 'submitted';
  }, [data, viewOnly]);

  /** Completed / view mode: only checklist rows the inspector activated (included). */
  const displayZones = useMemo(() => {
    if (!data?.zones) return [];
    if (!readOnly) return data.zones;
    return data.zones
      .map((z) => ({
        ...z,
        groups: z.groups
          .map((g) => ({
            ...g,
            questions: g.questions.filter((q) => included[q.id] === true),
          }))
          .filter((g) => g.questions.length > 0),
      }))
      .filter((z) => z.groups.length > 0);
  }, [data?.zones, readOnly, included]);

  const loadLocations = useCallback(async (propertyId) => {
    if (!propertyId) return;
    const res = await axios.get('/api/homesite/picker/locations', {
      headers: authHeader(),
      params: { propertyId },
    });
    setLocations(res.data || []);
  }, []);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`/api/homesite/inspections/${id}`, { headers: authHeader() });
      setData(res.data);
      const ins = res.data.inspection;
      await loadLocations(ins.propertyId);
      setInspectorName(ins.inspectorName || '');
      setInspectedAtLocal(toDatetimeLocal(ins.inspectedAt));
      setLocationId(ins.locationId ? String(ins.locationId) : '');
      setResidentId(ins.residentId ? String(ins.residentId) : '');
      setWarningNumber(ins.warningNumber || 'none');
      setDueDateCorrections(ins.dueDateCorrections ? String(ins.dueDateCorrections).slice(0, 10) : '');
      const inc = {};
      const a = {};
      const r = {};
      const n = {};
      for (const z of res.data.zones || []) {
        for (const g of z.groups || []) {
          for (const q of g.questions || []) {
            inc[q.id] = q.answer?.included === true;
            if (q.answer) {
              a[q.id] = q.answer.approval || '';
              r[q.id] = boolToYn(q.answer.resolved);
              n[q.id] = q.answer.note || '';
            } else {
              a[q.id] = '';
              r[q.id] = '';
              n[q.id] = '';
            }
          }
        }
      }
      setIncluded(inc);
      setApprovals(a);
      setResolved(r);
      setNotes(n);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id, loadLocations]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const residents = selectedLocation?.residents || [];
    if (residents.length === 1) setResidentId(String(residents[0].id));
    else if (!residents.some((r) => String(r.id) === String(residentId))) setResidentId('');
  }, [selectedLocation, residentId]);

  const flatQuestions = useMemo(() => {
    const list = [];
    if (!data?.zones) return list;
    for (const z of data.zones) {
      for (const g of z.groups || []) {
        for (const q of g.questions || []) {
          list.push({ zone: z.name, group: g.name, question: q });
        }
      }
    }
    return list;
  }, [data]);

  const buildAnswersPayload = () =>
    flatQuestions.map(({ question: q }) => {
      const on = included[q.id] === true;
      return {
        questionId: q.id,
        included: on,
        approval: on ? approvals[q.id] || null : null,
        resolved: on ? ynToBool(resolved[q.id]) : null,
        note: on ? notes[q.id] ?? '' : '',
      };
    });

  const toggleIncluded = (qid, on) => {
    if (readOnly) return;
    setIncluded((prev) => ({ ...prev, [qid]: on }));
    if (!on) {
      setApprovals((p) => ({ ...p, [qid]: '' }));
      setResolved((p) => ({ ...p, [qid]: '' }));
      setNotes((p) => ({ ...p, [qid]: '' }));
    }
  };

  const saveDraft = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      await axios.patch(
        `/api/homesite/inspections/${id}`,
        {
          inspectorName,
          inspectedAt: fromDatetimeLocal(inspectedAtLocal),
          locationId: locationId ? Number(locationId) : null,
          residentId: residentId ? Number(residentId) : null,
          warningNumber,
          dueDateCorrections: dueDateCorrections || null,
          answers: buildAnswersPayload(),
        },
        { headers: authHeader() }
      );
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      await saveDraft();
      await axios.post(`/api/homesite/inspections/${id}/submit`, {}, { headers: authHeader() });
      navigate(ROUTES.HOMESITE_REPORTS);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Discard this inspection? Unsaved changes may be lost.')) return;
    if (data?.inspection?.status === 'draft') {
      if (window.confirm('Delete this draft from the server?')) {
        try {
          await axios.delete(`/api/homesite/inspections/${id}`, { headers: authHeader() });
        } catch (_e) {
          /* still navigate */
        }
      }
    }
    navigate(ROUTES.HOMESITE);
  };

  const uploadPhoto = async (answerId, file) => {
    if (!file || readOnly) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      await axios.post(`/api/homesite/inspections/${id}/answers/${answerId}/images`, fd, {
        headers: { ...authHeader() },
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  const deletePhoto = async (imageId) => {
    if (readOnly) return;
    try {
      await axios.delete(`/api/homesite/inspections/${id}/images/${imageId}`, { headers: authHeader() });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="app-loading text-muted" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Loading inspection…</span>
      </div>
    );
  }
  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return null;

  const residents = selectedLocation?.residents || [];

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-warning py-2 mb-2">{error}</div>}
      {readOnly && <div className="alert alert-info py-2 mb-3">View only (submitted or read-only mode).</div>}

      <div className="card app-card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-4">
            <label className="form-label">Community</label>
            <input className="form-control" value={data.inspection.propertyName || ''} disabled />
          </div>
          <div className="col-md-4">
            <label className="form-label">Inspector</label>
            <input className="form-control" value={inspectorName} onChange={(e) => setInspectorName(e.target.value)} disabled={readOnly} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Date & time</label>
            <input type="datetime-local" className="form-control" value={inspectedAtLocal} onChange={(e) => setInspectedAtLocal(e.target.value)} disabled={readOnly} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Location</label>
            <select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} disabled={readOnly}>
              <option value="">Select location...</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.site_name || `Location ${l.id}`}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Resident</label>
            <select className="form-select" value={residentId} onChange={(e) => setResidentId(e.target.value)} disabled={readOnly}>
              <option value="">No resident selected</option>
              {residents.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Warning Number</label>
            <select className="form-select" value={warningNumber} onChange={(e) => setWarningNumber(e.target.value)} disabled={readOnly}>
              <option value="none">None</option>
              <option value="first">1st</option>
              <option value="second">2nd</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Due Date</label>
            <input type="date" className="form-control" value={dueDateCorrections} onChange={(e) => setDueDateCorrections(e.target.value)} disabled={readOnly} />
          </div>
        </div>
      </div>

      {displayZones.length === 0 && readOnly && (
        <p className="text-muted small">No activated inspection items on this report.</p>
      )}

      {displayZones.map((z) => (
        <div key={z.id} className="card app-card mb-4 bg-white">
          <div className="card-body">
            <h2 className="h5 text-primary border-bottom pb-2 mb-3">{z.name}</h2>
            {z.groups.map((g) => (
              <div key={g.id} className="card app-card mb-3 bg-white">
                <div className="card-body py-3">
                  <h3 className="h6 text-secondary mb-2">{g.name}</h3>
                  <ul className="list-group border rounded overflow-hidden mb-0">
                {g.questions.map((q) => {
                  const answer = q.answer;
                  const aid = answer?.id;
                  const isActivated = included[q.id] === true;
                  const showDetailFields = readOnly ? true : isActivated;
                  const checkboxChecked = isActivated;
                  return (
                    <li key={q.id} className="list-group-item px-3 py-3 inspection-homesite-item">
                      <div className="d-flex gap-3 align-items-start">
                        {!readOnly && (
                        <div
                          className={
                            `flex-shrink-0 d-flex align-items-center justify-content-center rounded-2 ${
                              checkboxChecked
                                ? 'pt-1'
                                : 'border border-2 border-primary shadow-sm bg-white p-2'
                            }`
                          }
                        >
                          <input
                            type="checkbox"
                            className="form-check-input flex-shrink-0 m-0"
                            style={
                              checkboxChecked
                                ? undefined
                                : {
                                    width: '1.35rem',
                                    height: '1.35rem',
                                    cursor: 'pointer',
                                  }
                            }
                            checked={checkboxChecked}
                            onChange={(e) => toggleIncluded(q.id, e.target.checked)}
                            aria-label={`Activate inspection item: ${q.questionText}`}
                          />
                        </div>
                        )}
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-medium">{q.questionText}</div>
                          {showDetailFields && (
                            <div className="row g-2 mt-3 align-items-start">
                              <div className="col-12 col-sm-6 col-md-3">
                                <label className="form-label small mb-1 text-dark">Approval</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={approvals[q.id] || ''}
                                  onChange={(e) =>
                                    setApprovals((prev) => ({ ...prev, [q.id]: e.target.value }))
                                  }
                                  disabled={readOnly}
                                >
                                  <option value="">Select…</option>
                                  <option value="pass">Pass</option>
                                  <option value="fail">Fail</option>
                                  <option value="na">NA</option>
                                </select>
                              </div>
                              <div className="col-12 col-sm-6 col-md-2">
                                <label className="form-label small mb-1 text-dark">Resolved</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={resolved[q.id] || ''}
                                  onChange={(e) =>
                                    setResolved((prev) => ({ ...prev, [q.id]: e.target.value }))
                                  }
                                  disabled={readOnly}
                                >
                                  <option value="">Select…</option>
                                  <option value="Y">Y</option>
                                  <option value="N">N</option>
                                </select>
                              </div>
                              <div className="col-12 col-md-4 inspection-q-note">
                                <label className="form-label small mb-1 text-dark">Note</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows={2}
                                  value={notes[q.id] ?? ''}
                                  onChange={(e) =>
                                    setNotes((prev) => ({ ...prev, [q.id]: e.target.value }))
                                  }
                                  disabled={readOnly}
                                />
                              </div>
                              <div className="col-12 col-md-3">
                                <label className="form-label small mb-1 text-dark">Picture</label>
                                {!readOnly && aid && (
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control form-control-sm"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = '';
                                      uploadPhoto(aid, f);
                                    }}
                                  />
                                )}
                                <div className="d-flex flex-column gap-1 mt-1">
                                  {(answer?.images || []).map((im) => (
                                    <div key={im.id} className="small">
                                      <a href={im.image_url} target="_blank" rel="noreferrer">
                                        View
                                      </a>
                                      {!readOnly && (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm text-danger p-0 ms-2"
                                          onClick={() => deletePhoto(im.id)}
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!readOnly && (
        <div className="d-flex flex-wrap gap-2 mb-4">
          <button type="button" className="btn btn-secondary" disabled={saving} onClick={saveDraft}>Save draft</button>
          <button type="button" className="btn btn-success" disabled={saving} onClick={submit}>Submit</button>
          <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={cancel}>Cancel</button>
        </div>
      )}

      {readOnly && (
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.HOMESITE_REPORTS)}>
          Back to reports
        </button>
      )}
    </div>
  );
}

export default HomesiteInspectionForm;
